import { useMemo } from 'react';
import { StatefulFunnelStep as StatefulFunnelStepFromPackage } from '@hotjar/global-filters';
import { ErrorBoundary } from '@hotjar/error-handler';
import type {
  AddFilterTippyInstance,
  FilterKindEnum,
  MinimalDateConstraint,
  ContextOfUse,
  GlobalFilter,
} from '@hotjar/global-filters';
import { isAllowed, getMinimumFeatureGroupForFeature } from '@hotjar/webapp/domains/common/feature';
import { useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { segment as trackingSegment } from '@hotjar/tracking';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { useIntegrationTokens } from '@hotjar/integrations';
import type { PlanComparisonReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { AdditionalTrackingData } from '@hotjar/dashboard-quantz';

export type StatefulFilterChainProps = {
  currentStepInUiFormat?: GlobalFilter;
  dateConstraint?: MinimalDateConstraint;
  supportedFilterList: Set<FilterKindEnum>;
  contextOfUse: ContextOfUse;
  isFiltersDropdownVisibleByDefault?: boolean;
  autoFocusAddFilter?: boolean;
  onChange: (filterInUiFormat: GlobalFilter) => void;
  onUpsellRequest: (
    featureName: PlanComparisonReferrer,
    additionalTrackingData?: AdditionalTrackingData,
  ) => void;
  setAddFilterTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
};

export const StatefulStep = ({
  currentStepInUiFormat,
  dateConstraint,
  supportedFilterList,
  contextOfUse,
  isFiltersDropdownVisibleByDefault,
  autoFocusAddFilter,
  onChange,
  onUpsellRequest,
  setAddFilterTippyInstance,
}: StatefulFilterChainProps): JSX.Element => {
  const { data: currentSite, isError: currentSiteIsError } = useCurrentSite();
  const { click, getGroupDataForSite, getPermissionLevelForSite } = useTrack();
  const { coveragePercentage } = useTrafficCoverage();

  const globalFilterContextValue = useMemo(
    () => ({
      useTrackResult: {
        click,
        getGroupDataForSite,
        getPermissionLevelForSite,
        track: trackingSegment.track,
      },
      useTrafficCoverageResult: { coveragePercentage },
      isAllowed,
      getMinimumFeatureGroupForFeature,
      useFeatureFlag,
      useIntegrationTokens,
    }),
    [click, coveragePercentage, getGroupDataForSite, getPermissionLevelForSite],
  );

  return (
    <ErrorBoundary squadSentryTag="product-foundations" featureName="funnel.step">
      <StatefulFunnelStepFromPackage
        currentStepInUiFormat={currentStepInUiFormat}
        currentSite={currentSite}
        currentSiteIsError={currentSiteIsError}
        onChange={onChange}
        onUpsellRequest={(featureName, kind) =>
          onUpsellRequest(featureName as PlanComparisonReferrer, {
            ui_position: `funnels.filters.${kind}`,
          })
        }
        setSelectStepTippyInstance={setAddFilterTippyInstance}
        dateConstraint={dateConstraint}
        supportedFilterList={supportedFilterList}
        contextOfUse={contextOfUse}
        isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
        autoFocusAddFilter={autoFocusAddFilter}
        contextObject={globalFilterContextValue}
      />
    </ErrorBoundary>
  );
};
